import type { AutomationToastCategory } from "./automationShowToast";

/**
 * The sound effects a user can pick for toasts, default first. Curated from
 * react-sounds' `notification/*` set (the id naming is kept for continuity):
 * `completed`/`success`/`error`/`warning` are left out because they read as
 * outcomes, not "something arrived".
 *
 * Self-hosted: playback (see @/lib/soundPlayer) fetches `/sounds/<id>.mp3`
 * directly from our own origin — see public/sounds/. Every id here needs its
 * own `public/sounds/<id>.mp3` (toastSound.asset.test.ts pins them). WEBV3-352:
 * this used to go through react-sounds' `playSound`, which fell back to a CDN
 * bucket that no longer exists and cached the resulting failure for the whole
 * page's life; soundPlayer.ts plays the local file directly instead.
 */
export const TOAST_SOUND_EFFECTS = [
  { id: "notification/info", label: "Info" },
  { id: "notification/message", label: "Message" },
  { id: "notification/notification", label: "Notification" },
  { id: "notification/reminder", label: "Reminder" },
  { id: "notification/popup", label: "Popup" },
] as const;

export type ToastSoundEffectId = (typeof TOAST_SOUND_EFFECTS)[number]["id"];

/** The effect used until a user picks another; what toasts played before WEBV3-306. */
export const DEFAULT_TOAST_SOUND_EFFECT: ToastSoundEffectId =
  "notification/info";

/**
 * Minimum gap between two toast sounds. Pitch: "the sound can only fire once
 * per second — if you get five in a row, that's one ding."
 */
export const TOAST_SOUND_MIN_GAP_MS = 1000;

/**
 * Leading-edge rate limit: the first sound in a window plays, every later one
 * inside the window is dropped.
 *
 * Pure so the limit can be tested without timers or React; the caller
 * (useToastSound) owns the last-played timestamp.
 * @param {number | null} lastPlayedAtMs - Epoch ms of the last sound that
 *   played, or null if none has yet.
 * @param {number} nowMs - Current epoch ms.
 * @param {number} [minGapMs] - Minimum gap between sounds.
 * @returns {boolean} True when a sound may play now.
 */
export function shouldPlayToastSound(
  lastPlayedAtMs: number | null,
  nowMs: number,
  minGapMs: number = TOAST_SOUND_MIN_GAP_MS
): boolean {
  if (lastPlayedAtMs === null) return true;
  const elapsedMs = nowMs - lastPlayedAtMs;
  // A clock that moved backwards must not silence sounds until it catches up.
  if (elapsedMs < 0) return true;
  return elapsedMs >= minGapMs;
}

/** What a toast sound is being played for. Each has its own on/off switch. */
export type ToastSoundCategory =
  | AutomationToastCategory
  | "customerUpdate"
  | "task";

export const TOAST_SOUND_CATEGORIES: readonly ToastSoundCategory[] = [
  "automation",
  "promiseTime",
  "customerUpdate",
  "task",
];

/** A user's stored sound settings (`users/{uid}.soundPreferences`). */
export type SoundPreferences = {
  effect: ToastSoundEffectId;
  enabled: Record<ToastSoundCategory, boolean>;
};

/** No stored value = today's behaviour: the default effect, every category on. */
export const DEFAULT_SOUND_PREFERENCES: SoundPreferences = {
  effect: DEFAULT_TOAST_SOUND_EFFECT,
  enabled: {
    automation: true,
    promiseTime: true,
    customerUpdate: true,
    task: true,
  },
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * The only reader of a stored `soundPreferences` value. Accepts anything and
 * always returns a complete, valid object: an unknown/missing effect becomes
 * the default (so it can never select a file that doesn't exist), and a
 * missing or non-boolean switch is ON (so a bad value can never silence a
 * category by accident). Unknown keys are dropped.
 * @param {unknown} raw - The stored value; may be undefined, partial, or garbage.
 * @returns {SoundPreferences} A fresh, fully populated object (never shared).
 */
export function resolveSoundPreferences(raw: unknown): SoundPreferences {
  const stored = isRecord(raw) ? raw : {};
  const storedEnabled = isRecord(stored.enabled) ? stored.enabled : {};

  const effect =
    TOAST_SOUND_EFFECTS.find((e) => e.id === stored.effect)?.id ??
    DEFAULT_TOAST_SOUND_EFFECT;

  const enabled = { ...DEFAULT_SOUND_PREFERENCES.enabled };
  for (const category of TOAST_SOUND_CATEGORIES) {
    const value = storedEnabled[category];
    if (typeof value === "boolean") enabled[category] = value;
  }

  return { effect, enabled };
}
